import React from "react";

import type { ISession } from "@mne-authentication/api";
import { Authenticated } from "@mne-authentication/session2";
import { BrowserRouter } from "react-router";

import { ArgusMenu, getMenuConfig, permissionCheckerUtil } from "@mnemonic/argus-menu";
import { getPackageBasename } from "@mnemonic/core-vite/utils";
import { SnackbarProvider } from "@mnemonic/feature-snackbar";
import { IntlProvider } from "@mnemonic/intl";
import type { IntlShape } from "@mnemonic/intl";
import { LogClient as Logger } from "@mnemonic/logging";
import type { TArgusLocale } from "@mnemonic/mui";
import type { IEnvConfig } from "@mnemonic/pure-utils";

import ApplicationSession from "./Components/ApplicationSession";
import InnerErrorBoundary from "./Components/ErrorBoundaries/InnerErrorBoundary";
import TopLevelErrorBoundary from "./Components/ErrorBoundaries/TopLevelErrorBoundary";
import { en, nb } from "./i18n/all";
import { ThemeProvider } from "./utils/ThemeProvider";

interface IApplicationProps {
  children: React.ReactNode;
  intl: IntlShape;
  session: ISession;
  target: IEnvConfig;
}

const Application = ({ children, intl, session, target }: IApplicationProps) => {
  return (
    <TopLevelErrorBoundary>
      <ApplicationSession session={session}>
        <Authenticated required={false}>
          {(session, sessionCtx) => {
            /* Hold off rendering until the session is verified. Splash screen should be displayed */
            if (sessionCtx.loading) {
              return null;
            }
            return (
              <ThemeProvider domain={session?.user?.domain} locale={intl.locale as TArgusLocale} session={session}>
                <IntlProvider locale={intl.locale} messages={intl.locale === "nb-NO" ? nb : en}>
                  <Authenticated required={false}>
                    {() => (
                      <BrowserRouter
                        basename={getPackageBasename(location.href, import.meta.env.DEV, import.meta.env.BASE_URL)}
                      >
                        <ArgusMenu
                          {...{
                            ...permissionCheckerUtil(
                              //@ts-expect-error The session type is compatible but gives type error should be resolved with ENG-53789
                              session,
                              getMenuConfig(
                                target.domain,
                                //@ts-expect-error The session type is compatible but gives type error should be resolved with ENG-53789
                                session?.user,
                                intl.locale as TArgusLocale,
                                Logger.getInstance(),
                              ),
                            ),
                            logger: Logger.getInstance(),
                          }}
                        >
                          <SnackbarProvider>
                            <InnerErrorBoundary>{children}</InnerErrorBoundary>
                          </SnackbarProvider>
                        </ArgusMenu>
                      </BrowserRouter>
                    )}
                  </Authenticated>
                </IntlProvider>
              </ThemeProvider>
            );
          }}
        </Authenticated>
      </ApplicationSession>
    </TopLevelErrorBoundary>
  );
};

export default Application;
